import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../actions/modalActions";
import { addManualItemToCart } from "../../actions/POS/cartActions";
import { FiPackage, FiX, FiAlertTriangle } from "react-icons/fi";

const ManualCartItemModal = () => {
  const dispatch = useDispatch();
  const { darkMode } = useSelector((state) => state.ui);
  const { loading } = useSelector((state) => state.cart);

  const [form, setForm] = useState({
    name: "",
    price: "",
    quantity: "1",
  });
  const [error, setError] = useState("");
  const nameRef = useRef(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError("");

    if (name === "price") {
      // Allow digits and a single decimal point
      if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
        setForm((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }

    if (name === "quantity") {
      // Positive integers only
      if (value === "" || /^[1-9]\d*$/.test(value)) {
        setForm((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Product name is required.";
    const price = parseFloat(form.price);
    if (!form.price || isNaN(price) || price <= 0)
      return "Please enter a valid price greater than 0.";
    const qty = parseInt(form.quantity);
    if (!form.quantity || isNaN(qty) || qty < 1)
      return "Quantity must be at least 1.";
    return "";
  };

  const handleAdd = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const manualItem = {
      id: `manual_${Date.now()}`,
      productId: null,
      name: form.name.trim(),
      price: parseFloat(parseFloat(form.price).toFixed(2)),
      quantity: parseInt(form.quantity),
      stock: 100,
      batchId: "0",
      Whcode: "",
      image: null,
      discountType: null,
      discountValue: 0,
      isManual: true,
    };

    const result = dispatch(addManualItemToCart(manualItem));

    if (!result?.success) {
      setError(result?.error || "Unable to add manual item.");
      return;
    }

    dispatch(closeModal());
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleAdd();
    if (e.key === "Escape") dispatch(closeModal());
  };

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors ${
    darkMode
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-800 placeholder-gray-400"
  }`;

  const labelClass = `block text-xs font-semibold mb-1 ${
    darkMode ? "text-gray-300" : "text-gray-600"
  }`;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div
        className={`w-full max-w-sm rounded-2xl shadow-2xl border ${
          darkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-5 py-4 border-b ${
            darkMode ? "border-gray-700" : "border-gray-100"
          }`}
        >
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                darkMode ? "bg-green-900/40" : "bg-green-50"
              }`}
            >
              <FiPackage className="w-4 h-4 text-green-500" />
            </div>
            <div>
              <h2
                className={`text-sm font-bold ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
              >
                Add Manual Item
              </h2>
              <p
                className={`text-xs ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Not from inventory
              </p>
            </div>
          </div>
          <button
            onClick={() => dispatch(closeModal())}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
              darkMode
                ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                : "hover:bg-gray-100 text-gray-400 hover:text-gray-700"
            }`}
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Product Name */}
          <div>
            <label className={labelClass}>Product Name *</label>
            <input
              ref={nameRef}
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="e.g. Custom Products"
              className={inputClass}
              maxLength={100}
            />
          </div>

          {/* Price and Quantity side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Price (Rs.) *</label>
              <input
                type="text"
                name="price"
                value={form.price}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="0.00"
                className={inputClass}
                inputMode="decimal"
              />
            </div>
            <div>
            <label className={labelClass}>Quantity *</label>
            <div className={`flex items-center rounded-lg border h-10 ${
                darkMode ? "border-gray-600" : "border-gray-300"
            }`}>
                <button
                    type="button"
                    onClick={() => {
                        const current = parseInt(form.quantity) || 1;
                        if (current > 1) setForm((prev) => ({ ...prev, quantity: String(current - 1) }));
                    }}
                    className={`w-9 h-full flex items-center justify-center text-base font-bold rounded-l-lg flex-shrink-0 transition-colors ${
                        darkMode
                            ? "bg-gray-600 text-gray-200 hover:bg-gray-500"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                    −
                </button>
                <input
                    type="text"
                    name="quantity"
                    value={form.quantity}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder="1"
                    inputMode="numeric"
                    className={`w-full h-full text-center text-sm bg-transparent focus:outline-none ${
                        darkMode ? "text-white" : "text-gray-800"
                    }`}
                />
                <button
                    type="button"
                    onClick={() => {
                        const current = parseInt(form.quantity) || 0;
                        setForm((prev) => ({ ...prev, quantity: String(current + 1) }));
                    }}
                    className={`w-9 h-full flex items-center justify-center text-base font-bold rounded-r-lg flex-shrink-0 transition-colors ${
                        darkMode
                            ? "bg-gray-600 text-gray-200 hover:bg-gray-500"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                    +
                </button>
            </div>
            </div>
          </div>

          {/* Total preview */}
          {form.price && form.quantity && !isNaN(parseFloat(form.price)) && !isNaN(parseInt(form.quantity)) && (
            <div
              className={`flex justify-between items-center px-3 py-2 rounded-lg text-sm ${
                darkMode ? "bg-gray-700/50" : "bg-gray-50"
              }`}
            >
              <span className={darkMode ? "text-gray-400" : "text-gray-500"}>
                Total
              </span>
              <span className="font-bold text-green-500">
                Rs.{" "}
                {(
                  parseFloat(form.price || 0) * parseInt(form.quantity || 1)
                ).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <FiAlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex gap-2 px-5 pb-5`}
        >
          <button
            onClick={() => dispatch(closeModal())}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              darkMode
                ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={loading}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold text-white transition-all ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:shadow-md active:scale-[0.98]"
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="fas fa-spinner fa-spin text-xs"></i> Adding...
              </span>
            ) : (
              "Add to Cart"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualCartItemModal;